// ============================================================================
// ActivityOverview — stat tiles, daily trend, and breakdowns (ORG-USAGE-2026)
//
// Chart decisions (see dataviz method):
//  • The four headline numbers are STAT TILES, not a chart — a single value per
//    measure has no shape to read.
//  • The daily trend is ONE series, so it is one hue and needs no legend; the
//    title names it. Hover gives the exact value rather than labeling every bar.
//  • Model / member / source breakdowns are ranked tables with an inline
//    magnitude bar — identity lives in the row label, so no categorical hues
//    are cycled.
//  • Never a dual axis: WU and spend are separate toggles on one scale.
//  • 2026-08-13 WU migration: no raw token counts render anywhere on this
//    surface. Work Units are derived client-side from the user-charged USD
//    the backend already returns (WU = usd / 2.18); systems render as
//    versionless STATION names via getPublicSystemName.
// ============================================================================

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  detectProviderFromModelId,
  getProviderColor,
  getPublicSystemName,
} from '../../config/providerCodeNames';
import type { ActivitySummaryResponse } from '../../services/rbacService';
// Round 10 (Miles 2026-08-13): every displayed value is click-to-copy via the
// page's own CopyText — plain text that inherits its cell's type exactly, a
// quiet "copied" pill on click, no icons, no popouts. (The wallet components
// tried here in round 8 truncated values and clipped names — see CopyText.)
import CopyText from './CopyText';
import {
  ACTIVITY_COLORS,
  BUCKET_OPTIONS,
  CARD_HOVER_STYLE,
  CARD_STYLE,
  CATEGORICAL_SERIES,
  FAMILY_SHADE_STEPS,
  INPUT_FOCUS_STYLE,
  OTHER_SERIES_COLOR,
  PAGE_GROUND,
  allowedBucketsForSpan,
  bucketWidthMs,
  formatDurationMs,
  formatUsd,
  formatWu,
  integerTicks,
  mixHex,
  timelineDisplayMode,
  wuFromUsd,
  WU_LABEL,
  type ActivityBucketChoice,
  type ActivityBucketValue,
} from './activityTheme';

/* One even tile row up top (round 11): the four headline numbers plus the
   three per-request averages, all the same tile family, wrapping evenly as
   the viewport narrows. The trend chart rides directly beneath. */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  /* Phones: two tiles per row instead of one long column. */
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }
`;

const Tile = styled.div`
  ${CARD_STYLE}
  padding: 1.125rem 1.25rem;
`;

const TileLabel = styled.div`
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  text-transform: lowercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.375rem;
`;

const TileValue = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  color: ${ACTIVITY_COLORS.ink};
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
`;

const TileSub = styled.div`
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  margin-top: 0.25rem;
`;

const Panel = styled.section`
  ${CARD_STYLE}
  ${CARD_HOVER_STYLE}
  padding: 1.25rem;
  min-width: 0;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

/* Full-width chart row: edge to edge under the top grid. */
const ChartPanel = styled(Panel)`
  margin-bottom: 1.5rem;
`;

const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const PanelTitle = styled.h2`
  /* Wallet section-title voice, scaled for dense analytics panels. */
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--brand-text);
  margin: 0;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const GranularitySelect = styled.select`
  font-size: 0.75rem;
  padding: 0.3125rem 0.5rem;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 0.5rem;
  background: ${PAGE_GROUND};
  color: ${ACTIVITY_COLORS.ink};
  cursor: pointer;
  font-family: inherit;
  ${INPUT_FOCUS_STYLE}
`;

const PanelHint = styled.span`
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  text-transform: lowercase;
`;

/* Escape hatch inside the by-member panel — drilling into a member must never
   feel like a dead end (owner feedback 2026-08-12). */
const BackLink = styled.button`
  background: none;
  border: none;
  color: ${ACTIVITY_COLORS.magnitude};
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
  text-decoration: underline;
  text-transform: lowercase;
`;

/* "you" marker (round 12): the viewer's own row in by-member reads at a
   glance — a soft gold wash on the row plus this small badge by the name. */
const YouBadge = styled.span`
  flex: none;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: #8a6d2a;
  background: rgba(198, 167, 94, 0.16);
  border: 1px solid rgba(198, 167, 94, 0.4);
  border-radius: 999px;
  padding: 0.0625rem 0.4375rem;
  text-transform: lowercase;
`;

const Toggle = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 6px;
  overflow: hidden;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  background: ${(p) => (p.$active ? ACTIVITY_COLORS.magnitude : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : ACTIVITY_COLORS.inkSecondary)};
  border: none;
  padding: 0.3125rem 0.75rem;
  font-size: 0.75rem;
  cursor: pointer;
  text-transform: lowercase;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => (p.$active ? ACTIVITY_COLORS.magnitude : ACTIVITY_COLORS.gridline)};
  }

  /* R21: in a window with no billable spend the toggles stay VISIBLE but
     inert — a test-only workspace keeps the same chrome as a billed one. */
  &:disabled {
    opacity: 0.45;
    cursor: default;
    background: transparent;
    color: ${ACTIVITY_COLORS.inkSecondary};
  }
`;

/* ── Bar chart ─────────────────────────────────────────────────────────────
   Thin marks, surface gaps between bars, 4px rounded data-end at the top,
   recessive hairline grid + y-ticks, hover tooltip with the full per-day
   breakdown. The plot always fits its container — 30/90-day ranges compress
   instead of growing a sideways scroll (owner feedback 2026-08-12). */
const CHART_HEIGHT = 'clamp(170px, 24vh, 300px)';

const ChartArea = styled.div`
  position: relative;
`;

const PlotRow = styled.div`
  display: flex;
  gap: 0.625rem;
  align-items: stretch;
`;

const YAxisCol = styled.div`
  position: relative;
  flex: none;
  width: 2.75rem;
  height: ${CHART_HEIGHT};
`;

const YTickLabel = styled.span<{ $pct: number }>`
  position: absolute;
  right: 0;
  bottom: ${(p) => p.$pct}%;
  transform: translateY(50%);
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const PlotBody = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

/* Hairline, solid, one-step-off-surface — recessive by construction. */
const GridLine = styled.div<{ $pct: number }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${(p) => p.$pct}%;
  height: 1px;
  background: ${ACTIVITY_COLORS.gridline};
  pointer-events: none;
`;

const Bars = styled.div<{ $dense: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-end;
  /* Round 13: when the (trimmed) window has few buckets, the capped-width
     bars huddle at the "now" end instead of stretching into slabs. */
  justify-content: flex-end;
  gap: ${(p) => (p.$dense ? '1px' : '2px')};
  /* Taller on roomy screens — the trend earns its landscape. */
  height: ${CHART_HEIGHT};
  border-bottom: 1px solid ${ACTIVITY_COLORS.border};
  padding-bottom: 0;
`;

/* Tooltip is declared before the columns so both column kinds can reveal it.
   Edge-aware: near the plot's edges it anchors to the column's edge instead
   of centering, so it never leaves the panel. */
const Tip = styled.div<{ $align: 'left' | 'center' | 'right' }>`
  position: absolute;
  bottom: calc(100% + 8px);
  ${(p) =>
    p.$align === 'left'
      ? 'left: 0;'
      : p.$align === 'right'
        ? 'right: 0;'
        : 'left: 50%; transform: translateX(-50%);'}
  background: ${ACTIVITY_COLORS.ink};
  color: #fff;
  font-size: 0.6875rem;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.1s ease;
  z-index: 5;
  font-variant-numeric: tabular-nums;
`;

const TipDate = styled.div`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.125rem;
  text-transform: lowercase;
`;

const TipTotal = styled.div`
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
`;

/* Value leads, label follows; identity rides a short line key, not text color. */
const TipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4375rem;
  line-height: 1.5;
`;

const TipKey = styled.span<{ $color: string }>`
  flex: none;
  width: 10px;
  height: 2px;
  border-radius: 1px;
  background: ${(p) => p.$color};
`;

const TipLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
`;

const TipValue = styled.span`
  font-weight: 600;
  margin-left: auto;
  padding-left: 0.75rem;
`;

const BarCol = styled.div<{ $zoomable?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  max-width: 3.5rem;
  height: 100%;
  display: flex;
  align-items: flex-end;
  position: relative;
  cursor: ${(p) => (p.$zoomable ? 'zoom-in' : 'default')};

  &:hover > div {
    background: ${ACTIVITY_COLORS.ink};
  }

  &:hover ${Tip} {
    opacity: 1;
  }
`;

const Bar = styled.div<{ $h: number; $c?: string }>`
  width: 100%;
  max-width: 3.5rem;
  margin-left: auto;
  margin-right: auto;
  height: ${(p) => Math.max(p.$h > 0 ? 2 : 0, p.$h)}%;
  background: ${(p) => p.$c ?? ACTIVITY_COLORS.magnitude};
  border-radius: 4px 4px 0 0;
  transition: background 0.15s ease;
`;

const XAxisTrack = styled.div`
  position: relative;
  height: 1.125rem;
  margin-top: 0.4375rem;
`;

const XTickLabel = styled.span<{ $pct: number }>`
  position: absolute;
  top: 0;
  left: ${(p) => p.$pct}%;
  transform: translateX(-50%);
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  white-space: nowrap;
`;

/* Round 14: the axis' right edge names the window's END — the literal word
   "now" for live windows (presets / rolling), so it is unmistakable that
   everything up to this moment is included. */
const XEdgeLabel = styled.span<{ $live: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0.6875rem;
  font-weight: ${(p) => (p.$live ? 600 : 400)};
  color: ${(p) => (p.$live ? ACTIVITY_COLORS.magnitude : ACTIVITY_COLORS.inkMuted)};
  white-space: nowrap;
`;

const EmptyNote = styled.div`
  color: ${ACTIVITY_COLORS.inkMuted};
  font-size: 0.8125rem;
  padding: 1.5rem 0;
  text-align: center;
`;

/* ── Ranked breakdown tables ───────────────────────────────────────────── */
const RankTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;

  th {
    text-align: left;
    font-weight: 500;
    color: ${ACTIVITY_COLORS.inkSecondary};
    padding: 0 0 0.5rem;
    border-bottom: 1px solid ${ACTIVITY_COLORS.border};
    text-transform: lowercase;
    font-size: 0.75rem;
  }

  td {
    padding: 0.5rem 0;
    border-bottom: 1px solid ${ACTIVITY_COLORS.gridline};
    color: ${ACTIVITY_COLORS.ink};
    font-variant-numeric: tabular-nums;
  }

  td:not(:first-child),
  th:not(:first-child) {
    text-align: right;
    white-space: nowrap;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const RankCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

/* Label row inside a rank cell. The label keeps to ONE line and ellipsizes
   only when genuinely long — never clips a final letter (round 10: the
   wallet copy components' padding games shaved the last glyph off names). */
const RankLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  max-width: 100%;

  .rank-label-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

`;

const RankBar = styled.div<{ $pct: number }>`
  height: 3px;
  width: ${(p) => Math.max(p.$pct > 0 ? 2 : 0, Math.min(100, p.$pct))}%;
  background: ${ACTIVITY_COLORS.magnitude};
  border-radius: 2px;
`;

const ShareCell = styled.span`
  color: ${ACTIVITY_COLORS.inkSecondary};
  font-variant-numeric: tabular-nums;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;

  /* Inside the wide layout, the remaining breakdowns span the full row. */
  @media (min-width: 1360px) {
    grid-column: 1 / -1;
  }
`;

/* Stacked segments: 2px surface gaps between segments (mark spec), rounded
   data-end only on the topmost segment. */
/* Round 14 accuracy: a segment's height IS its share — no per-segment floor
   and no height-adding gap margins (both made a $0.24 sliver look the same
   as a $1.15 band in small buckets). The divider is an inset hairline that
   costs no height and only paints when the segment can afford it. */
const StackSeg = styled.div<{ $h: number; $color: string; $top: boolean; $divider: boolean }>`
  width: 100%;
  height: ${(p) => p.$h}%;
  background: ${(p) => p.$color};
  border-radius: ${(p) => (p.$top ? '4px 4px 0 0' : '0')};
  box-shadow: ${(p) => (p.$divider && !p.$top ? 'inset 0 1px 0 rgba(255, 255, 255, 0.9)' : 'none')};
  transition: filter 0.1s ease;
`;

const StackCol = styled.div<{ $zoomable?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  /* Round 13 bar-width band: never slab-wide (a fat bar says nothing) — the
     flex-basis still shrinks freely for dense charts. */
  max-width: 3.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  cursor: ${(p) => (p.$zoomable ? 'zoom-in' : 'default')};

  /* The hovered day responds: a soft column band plus a slight lift on its
     segments, and the per-day breakdown tooltip appears. */
  &:hover {
    background: rgba(0, 0, 0, 0.045);
    border-radius: 4px 4px 0 0;
  }

  &:hover ${StackSeg} {
    filter: brightness(0.94);
  }

  &:hover ${Tip} {
    opacity: 1;
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
`;

const LegendChip = styled.span<{ $color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${(p) => p.$color};
  flex: none;
`;


// ── Axis helpers ─────────────────────────────────────────────────────────────
function niceStep(raw: number): number {
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  const factor = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return factor * pow;
}

/** Clean tick values from 0 up to (and just covering) max. */
function niceTicks(max: number, targetCount = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const step = niceStep(max / targetCount);
  const top = Math.ceil(max / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step * 1e-9; v += step) ticks.push(v);
  return ticks;
}

function yTickLabel(v: number, measure: 'wu' | 'usd'): string {
  if (v === 0) return '0';
  if (measure === 'wu') return formatWu(v);
  if (v < 1) return `$${v.toFixed(2)}`;
  if (v < 10 && !Number.isInteger(v)) return `$${v.toFixed(2).replace(/\.?0+$/, '')}`;
  return `$${Math.round(v).toLocaleString()}`;
}

const MONTH_LABELS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** "aug 5" from a bucket-start timestamp (UTC — matches backend day keys). */
function msDayLabel(ms: number): string {
  const d = new Date(ms);
  return `${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** "2pm" / "2:05pm" in LOCAL time (sub-day buckets align to the local range). */
function timeLabel(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hh}${ampm}` : `${hh}:${String(m).padStart(2, '0')}${ampm}`;
}

function bucketAxisLabel(ms: number, bucket: ActivityBucketValue): string {
  if (bucket === '1mo') return MONTH_LABELS[new Date(ms).getUTCMonth()];
  if (bucket === '1d' || bucket === '2d' || bucket === '1w') return msDayLabel(ms);
  return timeLabel(ms);
}

function bucketTipLabel(ms: number, bucket: ActivityBucketValue): string {
  if (bucket === '1mo') {
    const d = new Date(ms);
    return `${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  }
  if (bucket === '1d' || bucket === '2d' || bucket === '1w') return msDayLabel(ms);
  const d = new Date(ms);
  return `${MONTH_LABELS[d.getMonth()]} ${d.getDate()} · ${timeLabel(ms)}`;
}

/** Which day indices get an x label: evenly strided, endpoints always. */
function xTickIndices(n: number, maxLabels = 8): number[] {
  if (n <= 0) return [];
  if (n <= maxLabels) return Array.from({ length: n }, (_, i) => i);
  const stride = Math.ceil((n - 1) / (maxLabels - 1));
  const idxs: number[] = [];
  for (let i = 0; i < n; i += stride) idxs.push(i);
  const last = idxs[idxs.length - 1];
  if (last !== n - 1) {
    // keep the endpoint; drop the strided label if it would crowd it
    if (n - 1 - last >= stride / 2) idxs.push(n - 1);
    else idxs[idxs.length - 1] = n - 1;
  }
  return idxs;
}

type Measure = 'wu' | 'usd';
type ChartMode = 'total' | 'model';

interface Props {
  summary: ActivitySummaryResponse;
  /** "last 7 days" or "aug 1 – aug 9" — names the window under the tiles */
  rangeLabel?: string;
  /** requested chart granularity + its picker wiring (R19.5; round 13 adds
      'auto' — the chart picks a readable bucket itself) */
  bucket: ActivityBucketChoice;
  onBucketChange: (bucket: ActivityBucketChoice) => void;
  spanDays: number;
  /** true when the window's end edge is "now" (presets and rolling customs) */
  windowLive?: boolean;
  /** click-a-bar zoom (round 13): pin the clicked bucket's window */
  onZoomRange?: (startMs: number, endMs: number) => void;
  /** restore the window a zoom left — rendered as a back chip when depth > 0 */
  onZoomBack?: () => void;
  zoomDepth?: number;
  onSelectMember?: (memberId: string) => void;
  /** the member the page is currently scoped to (drives the escape hatch) */
  selectedMemberId?: string | null;
  onClearMemberScope?: () => void;
  /** the signed-in viewer — their own by-member row gets the "you" highlight */
  viewerMemberId?: string | null;
  /** R17: a fetch is running behind this (possibly stale) summary — dim the
      sections and soften every empty note into a quiet placeholder, so
      "loading" can never read as "no data". */
  refreshing?: boolean;
}

const ActivityOverview: React.FC<Props> = ({
  summary,
  rangeLabel,
  refreshing,
  bucket,
  onBucketChange,
  spanDays,
  windowLive = false,
  onZoomRange,
  onZoomBack,
  zoomDepth = 0,
  onSelectMember,
  selectedMemberId,
  onClearMemberScope,
  viewerMemberId,
}) => {
  // Defaults are the questions people actually arrive with: "what am I
  // spending, on which systems" — so spend + by-system, not WU + total.
  const [measure, setMeasure] = useState<Measure>('usd');
  const [chartMode, setChartMode] = useState<ChartMode>('model');

  // Normalized plot source (R19.5): the bucketed payload when the backend
  // sent one (any granularity, minute → month), else the legacy per-day
  // fields (rolling deploy). Keys are bucket-start timestamps either way.
  const plot = useMemo(() => {
    const b = summary.bucketed;
    if (b && b.series.length > 0) {
      // R22 (Miles): the chart draws EXACTLY the window the range control
      // states — quiet lead included. (The round-13 lead-trim died here: it
      // collapsed a stated 3.4-day window to wherever the data happened to
      // start, so the axis and the label disagreed.)
      const series = b.series;
      const modelSeries = b.byModel.series;
      return {
        bucket: b.bucket as ActivityBucketValue,
        approximate: b.approximate,
        keys: series.map((x) => x.startMs),
        totalsUsd: series.map((x) => x.usd),
        totalsTurns: series.map((x) => x.turns || 0),
        totalsTestTurns: series.map((x) => x.testTurns ?? 0),
        models: b.byModel.models,
        modelUsd: modelSeries.map((x) => x.usd),
      };
    }
    const toMs = (dayKey: string) => Date.parse(`${dayKey}T00:00:00.000Z`);
    return {
      bucket: '1d' as ActivityBucketValue,
      approximate: false,
      keys: (summary.dailySeries ?? []).map((d) => toMs(d.dayKey)),
      totalsUsd: (summary.dailySeries ?? []).map((d) => d.usd),
      totalsTurns: (summary.dailySeries ?? []).map((d) => d.turns || 0),
      totalsTestTurns: (summary.dailySeries ?? []).map(() => 0),
      models: summary.dailyByModel?.models ?? [],
      modelUsd: (summary.dailyByModel?.series ?? []).map((d) => d.usd),
    };
  }, [summary]);

  // STATION-merged stacked series. The backend stacks by RAW model id; the
  // station map is many-to-one — several internal families can share one
  // display name (e.g. legacy spark + titan both resolve to Titan) — so raw-id
  // buckets must be merged again after renaming or one station would render
  // as several stripes. Only USD is carried: the WU measure is derived from
  // it at render (usd / 2.18).
  const stackedLabels = useMemo(() => {
    if (plot.models.length === 0) return null;
    const labelOf = (raw: string) =>
      raw === '__other__' ? 'other systems' : getPublicSystemName(raw);
    const labels: string[] = [];
    const rawsByLabel = new Map<string, string[]>();
    for (const raw of plot.models) {
      const label = labelOf(raw);
      if (!rawsByLabel.has(label)) {
        rawsByLabel.set(label, []);
        labels.push(label);
      }
      rawsByLabel.get(label)!.push(raw);
    }
    return { labels, rawsByLabel };
  }, [plot.models]);

  // ── Station colors: COLOR FOLLOWS THE FAMILY (R19.4) ──────────────────────
  // The same station must wear the same color on every surface — the events
  // table and ⓘ popup key their dots on the provider family, so the chart,
  // legend, and by-system table anchor to the SAME family color here (owner:
  // "the colors in daily spend by system must be consistent with the activity
  // section"). The family is derived from each station's RAW ids (stations
  // are versionless display words; the raw id is the reliable family
  // signal). Sibling stations within one family take deterministic
  // (alphabetical) shades of the family color, so a range change never
  // repaints anyone. Stations with no recognizable family fall back to the
  // fixed categorical ramp; 'unknown'/'other systems' stay the neutral gray.
  const systemColors = useMemo(() => {
    // station label → one representative raw id (first seen)
    const rawOfLabel = new Map<string, string>();
    for (const [label, raws] of stackedLabels?.rawsByLabel ?? []) {
      if (label !== 'other systems' && raws.length > 0) rawOfLabel.set(label, raws[0]);
    }
    for (const m of summary.byModel ?? []) {
      const label = getPublicSystemName(m.modelId || '');
      if (!rawOfLabel.has(label) && m.modelId) rawOfLabel.set(label, m.modelId);
    }
    const byFamily = new Map<string, string[]>();
    const unknownFamily: string[] = [];
    for (const [label, raw] of rawOfLabel) {
      const family = detectProviderFromModelId(raw);
      if (!family || family === 'unknown') {
        unknownFamily.push(label);
        continue;
      }
      const list = byFamily.get(family) ?? [];
      list.push(label);
      byFamily.set(family, list);
    }
    const map = new Map<string, string>();
    for (const [family, members] of byFamily) {
      members.sort((a, b) => a.localeCompare(b));
      const base = getProviderColor(family);
      members.forEach((label, i) => {
        map.set(label, mixHex(base, FAMILY_SHADE_STEPS[Math.min(i, FAMILY_SHADE_STEPS.length - 1)]));
      });
    }
    unknownFamily.sort((a, b) => a.localeCompare(b));
    unknownFamily.forEach((label, i) => {
      map.set(label, CATEGORICAL_SERIES[i % CATEGORICAL_SERIES.length]);
    });
    map.set('other systems', OTHER_SERIES_COLOR);
    return map;
  }, [stackedLabels, summary.byModel]);

  const colorOfSystem = (label: string): string =>
    systemColors.get(label) ?? OTHER_SERIES_COLOR;

  const stacked = useMemo(() => {
    if (!stackedLabels) return null;
    const { labels, rawsByLabel } = stackedLabels;
    const series = plot.keys.map((key, i) => {
      const bucketUsd = plot.modelUsd[i] ?? {};
      const values: Record<string, { usd: number }> = {};
      for (const label of labels) {
        let usd = 0;
        for (const raw of rawsByLabel.get(label)!) {
          usd += bucketUsd[raw] ?? 0;
        }
        values[label] = { usd };
      }
      return { key, values };
    });
    return {
      labels: labels.map((label) => ({ label, color: colorOfSystem(label) })),
      series,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackedLabels, plot, systemColors]);

  // White-label mandate: never render a raw vendor model id — or a versioned
  // codename. The station map is MANY-TO-ONE (whole internal families merge
  // into one station), so the backend's raw-id buckets must be merged by
  // station here — otherwise the same label renders on several rows with the
  // true total split between them. Re-sorting is required because merging
  // changes the ranking the backend produced. WU is usd-derived per row.
  const modelRows = useMemo(() => {
    const merged = new Map<string, { key: string; label: string; usd: number }>();
    for (const m of summary.byModel ?? []) {
      const label = getPublicSystemName(m.modelId || '');
      const row = merged.get(label) ?? { key: label, label, usd: 0 };
      row.usd += m.usd;
      merged.set(label, row);
    }
    return Array.from(merged.values()).sort((a, b) => b.usd - a.usd);
  }, [summary.byModel]);

  // The WU series is the USD series rescaled by a constant, derived here on
  // the client (backend contract unchanged).
  const valueOf = (usd: number) => (measure === 'wu' ? wuFromUsd(usd) : usd);
  const maxValue = useMemo(
    () => Math.max(measure === 'wu' ? wuFromUsd(0.01) : 0.01, ...plot.totalsUsd.map(valueOf)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plot, measure]
  );

  // The cadence the server ACTUALLY rendered (it coarsens over-wide asks) and
  // the choices this range honestly supports.
  const effectiveBucket = plot.bucket;
  const bucketChoices = allowedBucketsForSpan(spanDays);

  // Round 13 — click a bar to zoom into exactly that bucket's window.
  // Nothing finer than 5-minute bars is worth a zoom, so those don't invite.
  const zoomable = Boolean(onZoomRange) && effectiveBucket !== '1m' && effectiveBucket !== '5m';
  const bucketEndMs = (key: number): number => {
    if (effectiveBucket === '1mo') {
      const d = new Date(key);
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    }
    return key + bucketWidthMs(effectiveBucket);
  };
  const zoomTo = (key: number) => {
    if (zoomable && onZoomRange) onZoomRange(key, bucketEndMs(key));
  };
  const cadenceLabel = (b: ActivityBucketValue) =>
    BUCKET_OPTIONS.find((o) => o.value === b)?.label ?? 'per day';

  // Bars scale to the top TICK, not the raw max, so gridlines land on clean
  // numbers and the y axis actually answers "how much is that bar".
  const scale = useMemo(() => {
    const ticks = niceTicks(maxValue, 4);
    return { ticks, max: ticks[ticks.length - 1] || 1 };
  }, [maxValue]);

  // ── Timeline sync with the events list (2026-08-17 owner directive) ──────
  // The chart must reflect the SAME recorded activity the list shows. The
  // empty state used to key off the USD series alone, so recorded-but-unbilled
  // windows ($0-billed turns; accounts whose every turn is test mode) claimed
  // "no usage recorded" over a populated list. Three states now: 'spend'
  // (billable spend → the usual chart), 'activity' (recorded tasks, zero
  // spend → task-count bars), 'empty' (genuinely nothing recorded).
  const displayMode = useMemo(
    () =>
      timelineDisplayMode(
        plot.keys.map((_, i) => ({
          usd: plot.totalsUsd[i] || 0,
          turns: plot.totalsTurns[i] || 0,
          testTurns: plot.totalsTestTurns[i] || 0,
        }))
      ),
    [plot]
  );
  const activityCounts = useMemo(
    () => plot.keys.map((_, i) => (plot.totalsTurns[i] || 0) + (plot.totalsTestTurns[i] || 0)),
    [plot]
  );
  const countScale = useMemo(() => {
    const ticks = integerTicks(activityCounts.reduce((a, b) => Math.max(a, b), 0), 4);
    return { ticks, max: ticks[ticks.length - 1] || 1 };
  }, [activityCounts]);

  const fmt = (v: number) => (measure === 'wu' ? `${formatWu(v)} ${WU_LABEL}` : formatUsd(v));

  const tipAlign = (idx: number, n: number): 'left' | 'center' | 'right' =>
    idx < n * 0.2 ? 'left' : idx >= n * 0.8 ? 'right' : 'center';

  const renderYAxis = (
    ticks: number[] = scale.ticks,
    max: number = scale.max,
    label: (v: number) => string = (t) => yTickLabel(t, measure)
  ) => (
    <YAxisCol aria-hidden>
      {ticks.map((t) => (
        <YTickLabel key={t} $pct={(t / max) * 100}>
          {label(t)}
        </YTickLabel>
      ))}
    </YAxisCol>
  );

  const renderGrid = (ticks: number[] = scale.ticks, max: number = scale.max) =>
    ticks
      .filter((t) => t > 0)
      .map((t) => <GridLine key={t} $pct={(t / max) * 100} />);

  // Round 14: sub-day ticks carry their DATE whenever a tick lands on a new
  // local day (and always on the first tick) — a time-only axis across
  // midnight left the day ambiguous. The right edge names the window end:
  // "now" for live windows, the exact end otherwise; ticks that would crowd
  // the edge label yield to it.
  const renderXAxis = (keys: number[]) => {
    const isSubDay = !['1d', '2d', '1w', '1mo'].includes(effectiveBucket);
    const endMs = summary.rangeEndMs ?? null;
    const showEdge = endMs !== null || windowLive;
    const ticks = xTickIndices(keys.length).filter(
      (i) => !showEdge || ((i + 0.5) / keys.length) * 100 <= 88
    );
    let prevDayKey: string | null = null;
    const labelFor = (i: number): string => {
      const ms = keys[i];
      if (!isSubDay) return bucketAxisLabel(ms, effectiveBucket);
      const d = new Date(ms);
      const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const withDay = prevDayKey !== dayKey;
      prevDayKey = dayKey;
      return withDay
        ? `${MONTH_LABELS[d.getMonth()]} ${d.getDate()} · ${timeLabel(ms)}`
        : timeLabel(ms);
    };
    const edgeLabel = (): string => {
      if (windowLive) return 'now';
      if (endMs === null) return '';
      if (!isSubDay) return msDayLabel(endMs - 1);
      const d = new Date(endMs);
      return `${MONTH_LABELS[d.getMonth()]} ${d.getDate()} · ${timeLabel(endMs)}`;
    };
    return (
      <XAxisTrack aria-hidden>
        {ticks.map((i) => (
          <XTickLabel key={keys[i]} $pct={((i + 0.5) / keys.length) * 100}>
            {labelFor(i)}
          </XTickLabel>
        ))}
        {showEdge && <XEdgeLabel $live={windowLive}>{edgeLabel()}</XEdgeLabel>}
      </XAxisTrack>
    );
  };

  // Share column: each row as a % of the table's own total — the table's rows
  // are the denominator, so shares always sum to ~100% even when a truncated
  // event scan makes them differ from the tiles. WU is a constant rescale of
  // USD, so bar lengths and shares are identical under either measure and the
  // table ranks on USD alone.
  // Round 10 interaction contract: clicking a NAME (or anywhere else on a
  // drill row) drills into that member; clicking any VALUE copies it
  // (CopyText stopPropagation). No icons, no popouts. An account with no
  // profile name reads "unnamed member · oUZYZfY8…U1u1" — click copies the
  // FULL id — instead of a wall of raw uid.
  const renderRankRows = (
    rows: Array<{
      key: string;
      label: string;
      usd: number;
      count?: number;
      unnamedId?: string;
      isViewer?: boolean;
    }>,
    onClick?: (key: string) => void,
    colorOf?: (key: string) => string
  ) => {
    const max = Math.max(1e-9, ...rows.map((r) => r.usd));
    const total = rows.reduce((acc, r) => acc + r.usd, 0);
    const shortId = (id: string) => (id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id);
    return rows.slice(0, 12).map((row) => {
      const share = total > 0 ? (row.usd / total) * 100 : 0;
      return (
        <tr
          key={row.key}
          onClick={onClick ? () => onClick(row.key) : undefined}
          style={{
            ...(onClick ? { cursor: 'pointer' } : {}),
            ...(row.isViewer ? { background: 'rgba(198, 167, 94, 0.07)' } : {}),
          }}
        >
          <td>
            <RankCell>
              <RankLabel>
                {colorOf && <LegendChip $color={colorOf(row.key)} style={{ flex: 'none' }} />}
                {row.unnamedId ? (
                  <CopyText copyValue={row.unnamedId} className="rank-label-text">
                    <span style={{ color: ACTIVITY_COLORS.inkSecondary }}>
                      unnamed member · {shortId(row.unnamedId)}
                    </span>
                  </CopyText>
                ) : onClick ? (
                  <span className="rank-label-text">{row.label}</span>
                ) : (
                  <CopyText className="rank-label-text">{row.label}</CopyText>
                )}
                {row.isViewer && <YouBadge>you</YouBadge>}
              </RankLabel>
              <RankBar $pct={(row.usd / max) * 100} />
            </RankCell>
          </td>
          <td>
            <CopyText>{formatWu(wuFromUsd(row.usd))}</CopyText>
          </td>
          <td>
            <CopyText>{formatUsd(row.usd)}</CopyText>
          </td>
          <td>
            <ShareCell>
              <CopyText>{share >= 0.5 ? `${Math.round(share)}%` : share > 0 ? '<1%' : '0%'}</CopyText>
            </ShareCell>
          </td>
        </tr>
      );
    });
  };

  // R21 (2026-08-17 Miles, workspace consistency): a window whose only
  // activity is test mode renders the FULL page — same tiles, same panels,
  // same toggles — with clear "never billed" labeling, instead of the gutted
  // zeros-and-empty-notes layout that reads as missing data.
  const testUsage = summary.testUsage;
  const testOnlyWindow = summary.totals.turns === 0 && (testUsage?.turns ?? 0) > 0;
  const testMemberRankRows = (testUsage?.byMember ?? []).map((m) => ({
    key: m.memberId,
    label: m.displayName || m.email || 'unnamed member',
    usd: m.usd,
    isViewer: Boolean(viewerMemberId) && m.memberId === viewerMemberId,
    ...(m.displayName || m.email ? {} : { unnamedId: m.memberId }),
  }));
  const testModelRankRows = (testUsage?.byModel ?? []).map((m) => ({
    key: m.modelId,
    label: getPublicSystemName(m.modelId),
    usd: m.usd,
  }));
  const testModelColorOf = (key: string) => getProviderColor(detectProviderFromModelId(key));

  return (
    /* R17: while a refetch runs behind this content the whole overview dims a
       touch — stale numbers stay readable but visibly in flux (the page-level
       "updating…" chip names what's happening). */
    <div style={{ opacity: refreshing ? 0.72 : 1, transition: 'opacity 0.25s ease' }}>

      {testOnlyWindow && (
        <PanelHint style={{ margin: '0 0 1rem' }}>
          everything in this window ran in test mode — tasks are recorded for
          visibility and never billed. numbers below reflect that.
        </PanelHint>
      )}

      {/* Round 11 owner layout: ONE even tile row up top — the four headline
          numbers PLUS the three per-request averages as tiles of the same
          family — then the spend-by-system trend rides directly beneath it.
          The by-system ranking (deprioritized) moved below by-member. */}
      <Grid>
        <Tile>
          <TileLabel>work units</TileLabel>
          {/* WU is display-derived from the user-charged USD total (Standard
              Rate) — never from token counts. */}
          <TileValue>
            <CopyText>{formatWu(wuFromUsd(summary.totals.usd))}</CopyText>
          </TileValue>
          <TileSub>
            {testOnlyWindow
              ? `${(testUsage?.turns ?? 0).toLocaleString()} test tasks — never billed`
              : rangeLabel ?? `last ${summary.days} days`}
          </TileSub>
        </Tile>
        <Tile>
          <TileLabel>spend</TileLabel>
          <TileValue>
            <CopyText>{formatUsd(summary.totals.usd)}</CopyText>
          </TileValue>
          <TileSub>
            {testOnlyWindow
              ? `${(testUsage?.turns ?? 0).toLocaleString()} test tasks — never billed`
              : rangeLabel ?? `last ${summary.days} days`}
          </TileSub>
        </Tile>
        {summary.scope !== 'own' && (
          <Tile>
            <TileLabel>active members</TileLabel>
            <TileValue>
              <CopyText>
                {(testOnlyWindow
                  ? (testUsage?.byMember.length ?? 0)
                  : summary.totals.members
                ).toLocaleString()}
              </CopyText>
            </TileValue>
            <TileSub>{testOnlyWindow ? 'with test usage' : 'with usage'}</TileSub>
          </Tile>
        )}
        {/* Round 12: the money averages are EXACT over every request in
            the chosen period. R21: the tiles ALWAYS render — a workspace
            with nothing billed keeps the same layout as one with usage,
            values simply read "—" with the reason underneath. */}
        <Tile>
          <TileLabel>avg {WU_LABEL} / task</TileLabel>
          <TileValue>
            {summary.totals.turns > 0 ? (
              <CopyText>
                {formatWu(wuFromUsd(summary.totals.usd / summary.totals.turns))}
              </CopyText>
            ) : (
              '—'
            )}
          </TileValue>
          <TileSub>
            {summary.totals.turns > 0
              ? `all ${summary.totals.turns.toLocaleString()} requests`
              : testOnlyWindow
                ? 'test tasks only'
                : 'no billed tasks in range'}
          </TileSub>
        </Tile>
        <Tile>
          <TileLabel>avg cost / task</TileLabel>
          <TileValue>
            {summary.totals.turns > 0 ? (
              <CopyText>{formatUsd(summary.totals.usd / summary.totals.turns)}</CopyText>
            ) : (
              '—'
            )}
          </TileValue>
          <TileSub>
            {summary.totals.turns > 0
              ? `all ${summary.totals.turns.toLocaleString()} requests`
              : testOnlyWindow
                ? 'test tasks only'
                : 'no billed tasks in range'}
          </TileSub>
        </Tile>
        {/* Turn time only exists on the events themselves, so this one
            keeps the scan basis and says so when the scan was capped. */}
        <Tile>
          <TileLabel>avg task time</TileLabel>
          <TileValue>
            {summary.stats && summary.stats.avgDurationMs !== null ? (
              <CopyText>{formatDurationMs(summary.stats.avgDurationMs)}</CopyText>
            ) : (
              '—'
            )}
          </TileValue>
          <TileSub>
            {summary.stats && summary.stats.avgDurationMs !== null
              ? summary.statsTruncated
                ? 'most recent 2,000 requests'
                : 'request to reply'
              : testOnlyWindow
                ? 'test tasks only'
                : 'no billed tasks in range'}
          </TileSub>
        </Tile>
      </Grid>

      <ChartPanel>
        <PanelHead>
          <PanelTitle>
            {displayMode === 'activity'
              ? 'activity'
              : measure === 'wu' ? 'pr0' : 'spend'}
            {/* suffix only when the stacked chart actually renders — without
                per-system buckets the mode silently falls back to total */}
            {displayMode !== 'activity' && chartMode === 'model' && stacked ? ' by system' : ''}
          </PanelTitle>
          {/* Defaults lead each toggle: by system before total, spend before
              WU — the default option always sits on the left. */}
          <span style={{ display: 'inline-flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {zoomDepth > 0 && onZoomBack && (
              <BackLink onClick={onZoomBack}>← back to wider view</BackLink>
            )}
            <GranularitySelect
              value={bucket}
              onChange={(e) => onBucketChange(e.target.value as ActivityBucketChoice)}
              aria-label="chart granularity"
            >
              {/* auto leads: the chart picks a readable cadence itself and
                  the label says which one it picked (round 13). */}
              <option value="auto">auto · {cadenceLabel(effectiveBucket)}</option>
              {BUCKET_OPTIONS.filter((o) => bucketChoices.includes(o.value)).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </GranularitySelect>
            {/* spend/WU toggles say nothing about a window with no billable
                spend — hidden while the task-count timeline renders. */}
            {(stacked || displayMode === 'activity') && (
              <Toggle
                role="group"
                aria-label="chart mode"
                title={displayMode === 'activity' ? 'no billable spend in this window' : undefined}
              >
                <ToggleBtn
                  $active={displayMode !== 'activity' && chartMode === 'model'}
                  disabled={displayMode === 'activity'}
                  onClick={() => setChartMode('model')}
                >
                  by system
                </ToggleBtn>
                <ToggleBtn
                  $active={displayMode !== 'activity' && chartMode === 'total'}
                  disabled={displayMode === 'activity'}
                  onClick={() => setChartMode('total')}
                >
                  total
                </ToggleBtn>
              </Toggle>
            )}
            <Toggle
              role="group"
              aria-label="measure"
              title={displayMode === 'activity' ? 'no billable spend in this window' : undefined}
            >
              <ToggleBtn
                $active={displayMode !== 'activity' && measure === 'usd'}
                disabled={displayMode === 'activity'}
                onClick={() => setMeasure('usd')}
              >
                spend
              </ToggleBtn>
              <ToggleBtn
                $active={displayMode !== 'activity' && measure === 'wu'}
                disabled={displayMode === 'activity'}
                onClick={() => setMeasure('wu')}
              >
                {WU_LABEL}
              </ToggleBtn>
            </Toggle>
          </span>
        </PanelHead>

        {bucket !== 'auto' && effectiveBucket !== bucket && (
          <PanelHint>
            shown {cadenceLabel(effectiveBucket)} — the range is too wide for {cadenceLabel(bucket)}
          </PanelHint>
        )}
        {plot.approximate && (
          <PanelHint>
            covers the newest 2,000 requests in this window — narrow the range for exact bands
          </PanelHint>
        )}
        {displayMode === 'activity' && (
          <PanelHint>
            {plot.totalsTurns.every((v) => !v)
              ? 'test tasks only in this window — recorded, never billed. bars show task counts.'
              : 'recorded activity with no billable spend in this window — bars show task counts.'}
          </PanelHint>
        )}
        {displayMode === 'empty' ? (
          <EmptyNote>
            {refreshing ? 'checking this period…' : 'no usage recorded in this period yet.'}
          </EmptyNote>
        ) : displayMode === 'activity' ? (
          <ChartArea>
            <PlotRow>
              {renderYAxis(countScale.ticks, countScale.max, (v) =>
                v === 0 ? '0' : v.toLocaleString()
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <PlotBody>
                  {renderGrid(countScale.ticks, countScale.max)}
                  <Bars $dense={plot.keys.length > 45}>
                    {plot.keys.map((key, idx) => {
                      const billable = plot.totalsTurns[idx] || 0;
                      const test = plot.totalsTestTurns[idx] || 0;
                      const count = billable + test;
                      const noun = count === 1 ? 'task' : 'tasks';
                      return (
                        <BarCol key={key} $zoomable={zoomable} onClick={() => zoomTo(key)}>
                          <Bar
                            $h={(count / countScale.max) * 100}
                            $c={billable > 0 ? undefined : OTHER_SERIES_COLOR}
                          />
                          <Tip $align={tipAlign(idx, plot.keys.length)}>
                            <TipDate>{bucketTipLabel(key, effectiveBucket)}</TipDate>
                            <TipTotal>{`${count.toLocaleString()} ${noun}`}</TipTotal>
                            {billable > 0 && test > 0 && (
                              <>
                                <TipRow>
                                  <TipKey $color={ACTIVITY_COLORS.magnitude} />
                                  <TipLabel>recorded</TipLabel>
                                  <TipValue>{billable.toLocaleString()}</TipValue>
                                </TipRow>
                                <TipRow>
                                  <TipKey $color={OTHER_SERIES_COLOR} />
                                  <TipLabel>test</TipLabel>
                                  <TipValue>{test.toLocaleString()}</TipValue>
                                </TipRow>
                              </>
                            )}
                            {zoomable && <TipDate>click to zoom in</TipDate>}
                          </Tip>
                        </BarCol>
                      );
                    })}
                  </Bars>
                </PlotBody>
                {renderXAxis(plot.keys)}
              </div>
            </PlotRow>
          </ChartArea>
        ) : chartMode === 'model' && stacked ? (
          <ChartArea>
            <PlotRow>
              {renderYAxis()}
              <div style={{ flex: 1, minWidth: 0 }}>
                <PlotBody>
                  {renderGrid()}
                  <Bars $dense={stacked.series.length > 45}>
                    {stacked.series.map((day, idx) => {
                      const dayTotal = stacked.labels.reduce(
                        (acc, { label }) => acc + valueOf(day.values[label].usd),
                        0
                      );
                      return (
                        <StackCol
                          key={day.key}
                          $zoomable={zoomable}
                          onClick={() => zoomTo(day.key)}
                        >
                          {/* bottom-up: reverse so slot 1 sits on the baseline.
                              The COLUMN gets a small visibility floor; its
                              segments then split that height exactly to
                              size, so shares read true at any bar height
                              (round 14 accuracy). */}
                          {(() => {
                            const columnPct = (dayTotal / scale.max) * 100;
                            const renderPct = dayTotal > 0 ? Math.max(columnPct, 1.5) : 0;
                            let topAssigned = false;
                            return [...stacked.labels].reverse().map(({ label, color }) => {
                              const v = valueOf(day.values[label].usd);
                              const segPct = dayTotal > 0 ? (v / dayTotal) * renderPct : 0;
                              const isTop = !topAssigned && v > 0;
                              if (isTop) topAssigned = true;
                              return (
                                <StackSeg
                                  key={label}
                                  $h={segPct}
                                  $color={color}
                                  $top={isTop}
                                  $divider={segPct >= 0.8}
                                />
                              );
                            });
                          })()}
                          <Tip $align={tipAlign(idx, stacked.series.length)}>
                            <TipDate>{bucketTipLabel(day.key, effectiveBucket)}</TipDate>
                            <TipTotal>{fmt(dayTotal)}</TipTotal>
                            {stacked.labels
                              .filter(({ label }) => day.values[label].usd > 0)
                              .map(({ label, color }) => (
                                <TipRow key={label}>
                                  <TipKey $color={color} />
                                  <TipLabel>{label}</TipLabel>
                                  <TipValue>{fmt(valueOf(day.values[label].usd))}</TipValue>
                                </TipRow>
                              ))}
                            {zoomable && <TipDate>click to zoom in</TipDate>}
                          </Tip>
                        </StackCol>
                      );
                    })}
                  </Bars>
                </PlotBody>
                {renderXAxis(stacked.series.map((d) => d.key))}
              </div>
            </PlotRow>
            {/* Legend is ALWAYS present for multi-series (identity never rides
                color alone — the ranked by-model table below is the relief for
                low-contrast fills). */}
            <Legend>
              {stacked.labels.map(({ label, color }) => (
                <LegendItem key={label}>
                  <LegendChip $color={color} />
                  {label}
                </LegendItem>
              ))}
            </Legend>
          </ChartArea>
        ) : (
          <ChartArea>
            <PlotRow>
              {renderYAxis()}
              <div style={{ flex: 1, minWidth: 0 }}>
                <PlotBody>
                  {renderGrid()}
                  <Bars $dense={plot.keys.length > 45}>
                    {plot.keys.map((key, idx) => {
                      const value = valueOf(plot.totalsUsd[idx]);
                      return (
                        <BarCol key={key} $zoomable={zoomable} onClick={() => zoomTo(key)}>
                          <Bar $h={(value / scale.max) * 100} />
                          <Tip $align={tipAlign(idx, plot.keys.length)}>
                            <TipDate>{bucketTipLabel(key, effectiveBucket)}</TipDate>
                            <TipTotal>{fmt(value)}</TipTotal>
                            {zoomable && <TipDate>click to zoom in</TipDate>}
                          </Tip>
                        </BarCol>
                      );
                    })}
                  </Bars>
                </PlotBody>
                {renderXAxis(plot.keys)}
              </div>
            </PlotRow>
          </ChartArea>
        )}
      </ChartPanel>

        {/* by-member spans the full row below the chart. (The old by-source
            panel is gone — sources never informed a decision; the events
            table's source filter remains for the rare forensic case.) */}
        {summary.scope !== 'own' && (
          <Panel style={{ marginBottom: '1.5rem' }}>
            <PanelHead>
              <PanelTitle>by member</PanelTitle>
              {selectedMemberId && onClearMemberScope ? (
                <BackLink onClick={onClearMemberScope}>← show everyone</BackLink>
              ) : (
                onSelectMember &&
                summary.byMember.length > 0 && (
                  <PanelHint>select a member to see just their activity</PanelHint>
                )
              )}
            </PanelHead>
            {summary.byMember.length === 0 ? (
              testMemberRankRows.length > 0 ? (
                <>
                  <PanelHint>test tasks — recorded, never billed.</PanelHint>
                  <RankTable>
                    <thead>
                      <tr>
                        <th>member</th>
                        <th>work units</th>
                        <th>spend</th>
                        <th>share</th>
                      </tr>
                    </thead>
                    <tbody>{renderRankRows(testMemberRankRows, onSelectMember)}</tbody>
                  </RankTable>
                </>
              ) : (
                <EmptyNote>{refreshing ? 'checking…' : 'nothing yet.'}</EmptyNote>
              )
            ) : (
              <RankTable>
                <thead>
                  <tr>
                    <th>member</th>
                    <th>work units</th>
                    <th>spend</th>
                    <th>share</th>
                  </tr>
                </thead>
                <tbody>
                  {renderRankRows(
                    (() => {
                      const rows = summary.byMember.map((m) => ({
                        key: m.memberId,
                        label: m.displayName || m.email || 'unnamed member',
                        usd: m.usd,
                        isViewer: Boolean(viewerMemberId) && m.memberId === viewerMemberId,
                        // No resolved profile name → friendly label + short id,
                        // click copies the full id (round 10: never a wall of
                        // raw uid, never a popout).
                        ...(m.displayName || m.email ? {} : { unnamedId: m.memberId }),
                      }));
                      // The viewer's own row must always be visible (round 12)
                      // — if they rank below the 12-row cut, they take the
                      // last slot with their true numbers.
                      const viewerIdx = rows.findIndex((r) => r.isViewer);
                      if (viewerIdx >= 12) {
                        const viewerRow = rows[viewerIdx];
                        return [...rows.slice(0, 11), viewerRow];
                      }
                      return rows;
                    })(),
                    onSelectMember
                  )}
                </tbody>
              </RankTable>
            )}
          </Panel>
        )}

        {/* by-system, deprioritized (round 11): below by-member, above the
            events feed. Same rank table, chart-consistent colors. */}
        <Panel style={{ marginBottom: '1.5rem' }}>
          <PanelHead>
            <PanelTitle>by system</PanelTitle>
          </PanelHead>
          {modelRows.length === 0 ? (
            testModelRankRows.length > 0 ? (
              <>
                <PanelHint>test tasks — recorded, never billed.</PanelHint>
                <RankTable>
                  <thead>
                    <tr>
                      <th>system</th>
                      <th>work units</th>
                      <th>spend</th>
                      <th>share</th>
                    </tr>
                  </thead>
                  <tbody>{renderRankRows(testModelRankRows, undefined, testModelColorOf)}</tbody>
                </RankTable>
              </>
            ) : (
              <EmptyNote>{refreshing ? 'checking…' : 'nothing yet.'}</EmptyNote>
            )
          ) : (
            <RankTable>
              <thead>
                <tr>
                  <th>system</th>
                  <th>work units</th>
                  <th>spend</th>
                  <th>share</th>
                </tr>
              </thead>
              <tbody>
                {renderRankRows(modelRows, undefined, colorOfSystem)}
              </tbody>
            </RankTable>
          )}
        </Panel>
    </div>
  );
};

export default ActivityOverview;
